import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Search,
  Plus,
  Filter,
  Calendar,
  User,
  AlertCircle,
  Clock,
  CheckCircle2,
  ChevronDown,
} from 'lucide-react';
import api from '../services/api';
import { Job, PaginatedResponse, JobStatus, TicketPriority } from '../types';
import Layout from '../components/layout/Layout';

const statusConfig: Record<JobStatus, { label: string; color: string; bgColor: string; icon: typeof AlertCircle }> = {
  RED: { label: 'Action Required', color: 'text-status-red', bgColor: 'bg-status-red', icon: AlertCircle },
  YELLOW: { label: 'In Progress', color: 'text-status-yellow', bgColor: 'bg-status-yellow', icon: Clock },
  GREEN: { label: 'Completed', color: 'text-status-green', bgColor: 'bg-status-green', icon: CheckCircle2 },
};

const priorityColors: Record<TicketPriority, string> = {
  LOW: 'bg-dark-muted/20 text-dark-muted',
  MEDIUM: 'bg-primary-500/20 text-primary-500',
  HIGH: 'bg-status-yellow/20 text-status-yellow',
  URGENT: 'bg-status-red/20 text-status-red',
};

export default function Jobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [viewMode, setViewMode] = useState<'pipeline' | 'list'>('pipeline');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, priorityFilter]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: '100',
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(priorityFilter && { priority: priorityFilter }),
      });

      const response = await api.get<PaginatedResponse<Job>>(`/jobs?${params}`);
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchJobs();
  };

  const getJobsByStatus = (status: JobStatus) => jobs.filter((job) => job.status === status);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return date.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });
  };

  const JobCard = ({ job }: { job: Job }) => (
    <div
      onClick={() => navigate(`/jobs/${job.id}`)}
      className="bg-dark-elevated rounded-lg p-4 cursor-pointer hover:bg-dark-border/50 transition-colors group"
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-mono text-dark-muted">{job.ticket_number}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${priorityColors[job.priority]}`}>
          {job.priority}
        </span>
      </div>
      <h4 className="font-medium mb-1 group-hover:text-primary-500 transition-colors">{job.title}</h4>
      <p className="text-sm text-dark-muted mb-3">{job.customer_name}</p>
      <div className="flex items-center justify-between text-xs text-dark-muted">
        <div className="flex items-center gap-1">
          <Calendar size={12} />
          <span>{job.due_date ? formatDate(job.due_date) : 'No due date'}</span>
        </div>
        {job.assigned_to_name && (
          <div className="flex items-center gap-1">
            <User size={12} />
            <span>{job.assigned_to_name.split(' ')[0]}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Briefcase className="text-primary-500" size={28} />
            <h1 className="text-2xl font-semibold">Jobs Pipeline</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg overflow-hidden border border-dark-border">
              <button
                onClick={() => setViewMode('pipeline')}
                className={`px-4 py-2 text-sm ${
                  viewMode === 'pipeline' ? 'bg-primary-500 text-white' : 'bg-dark-surface text-dark-muted'
                }`}
              >
                Pipeline
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 text-sm ${
                  viewMode === 'list' ? 'bg-primary-500 text-white' : 'bg-dark-surface text-dark-muted'
                }`}
              >
                List
              </button>
            </div>
            <button
              onClick={() => navigate('/jobs/new')}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              New Job
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="card mb-6">
          <div className="flex gap-4 items-center">
            <form onSubmit={handleSearch} className="flex gap-4 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted" size={20} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search jobs..."
                  className="input pl-10 w-full"
                />
              </div>
              <button type="submit" className="btn btn-secondary">
                Search
              </button>
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
            >
              <Filter size={18} />
              Filters
              <ChevronDown size={16} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="flex gap-4 mt-4 pt-4 border-t border-dark-border">
              <div>
                <label className="block text-sm text-dark-muted mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as JobStatus | '')}
                  className="input"
                >
                  <option value="">All Statuses</option>
                  <option value="RED">Action Required</option>
                  <option value="YELLOW">In Progress</option>
                  <option value="GREEN">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-dark-muted mb-1">Priority</label>
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value as TicketPriority | '')}
                  className="input"
                >
                  <option value="">All Priorities</option>
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
              <button
                onClick={() => {
                  setStatusFilter('');
                  setPriorityFilter('');
                }}
                className="btn btn-ghost self-end"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Pipeline View */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : viewMode === 'pipeline' ? (
          <div className="grid grid-cols-3 gap-6">
            {(['RED', 'YELLOW', 'GREEN'] as JobStatus[]).map((status) => {
              const config = statusConfig[status];
              const statusJobs = getJobsByStatus(status);

              return (
                <div key={status} className="flex flex-col">
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-3 h-3 rounded-full ${config.bgColor}`} />
                    <h3 className={`font-medium ${config.color}`}>{config.label}</h3>
                    <span className="text-sm text-dark-muted">({statusJobs.length})</span>
                  </div>
                  <div className="flex-1 space-y-3 min-h-[200px] p-3 bg-dark-surface/50 rounded-lg border border-dark-border/50">
                    {statusJobs.length === 0 ? (
                      <div className="text-center py-8 text-dark-muted text-sm">No jobs</div>
                    ) : (
                      statusJobs.map((job) => <JobCard key={job.id} job={job} />)
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="card">
            {jobs.length === 0 ? (
              <div className="text-center py-12 text-dark-muted">
                <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
                <p>No jobs found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-dark-border">
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Job #</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Title</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Customer</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Priority</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Due Date</th>
                      <th className="text-left py-3 px-4 font-medium text-dark-muted">Assigned To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <tr
                        key={job.id}
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        className="border-b border-dark-border/50 hover:bg-dark-elevated/50 cursor-pointer transition-colors"
                      >
                        <td className="py-4 px-4 font-mono text-sm">{job.ticket_number}</td>
                        <td className="py-4 px-4">{job.title}</td>
                        <td className="py-4 px-4 text-dark-muted">{job.customer_name}</td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${statusConfig[job.status].bgColor}`} />
                            <span className={statusConfig[job.status].color}>
                              {statusConfig[job.status].label}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${priorityColors[job.priority]}`}>
                            {job.priority}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-dark-muted">{formatDate(job.due_date)}</td>
                        <td className="py-4 px-4 text-dark-muted">{job.assigned_to_name || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
